<template>
  <v-main>
    <v-container fluid>
      <div class="flex items-center pl-3">
        <div class="text-xl">{{ t("common.supervisorOrgs") }}</div>
        <v-spacer></v-spacer>
        <v-text-field
          v-model="searchQuery"
          :label="t('common.search')"
          prepend-inner-icon="mdi-magnify"
          hide-details
          density="compact"
          class="mr-3"
          style="max-width: 300px"
        ></v-text-field>
        <v-btn
          class="mx-auto"
          variant="flat"
          color="primary"
          height="40px"
          append-icon="mdi-plus"
          data-testid="admin-supervisor-orgs-add-button"
          @click="showAdd = true"
          >{{ t("common.add") }}</v-btn
        >
      </div>

      <v-data-table-virtual
        :headers="headers"
        :items="supervisorOrgsWithActiveAccountsCount"
        :loading="loading"
        :search="searchQuery"
        :sort-by="[{ key: 'name' }]"
        hover
        class="mt-6"
      >
        <template #item="{ item, props: rowProps, itemRef }">
          <RowLink
            v-bind="rowProps"
            :ref="itemRef"
            :to="{ name: 'Accounts', query: { supervisorOrgId: item.id } }"
          >
            <RowLinkCell focusable>{{ item.name }}</RowLinkCell>
            <RowLinkCell>{{ item.activeAccountsCount }}</RowLinkCell>
          </RowLink>
        </template>
      </v-data-table-virtual>
      <AddSupervisorOrg v-model="showAdd"></AddSupervisorOrg>
    </v-container>
  </v-main>
</template>

<script lang="ts" setup>
import RowLink from "@/components/RowLink/RowLink.vue";
import RowLinkCell from "@/components/RowLink/RowLinkCell.vue";
import { useCollection } from "@/composables/useFirestore";
import { useI18n } from "@/composables/useI18n";
import { db } from "@/firebase";
import type { Account } from "@aircap/common/account";
import type { SupervisorOrg } from "@aircap/common/supervisorOrg";
import { supervisorOrgsFullPath } from "@aircap/common/supervisorOrg";
import { collection, query, where } from "firebase/firestore";
import { computed, ref } from "vue";
import AddSupervisorOrg from "./AddSupervisorOrg.vue";

const supervisorOrgsRef = collection<SupervisorOrg>(db, supervisorOrgsFullPath);
const activeAccountsRef = query(
  collection<Account>(db, "accounts"),
  where<Account>("isArchived", "==", false),
);
const searchQuery = ref("");
const showAdd = ref(false);
const { t } = useI18n();

const headers = computed(() => [
  { title: t("common.name"), key: "name" },
  { title: t("common.accounts"), key: "activeAccountsCount" },
]);

const { data: supervisorOrgs, pending: loadingSupervisorOrgs } =
  useCollection<SupervisorOrg>(supervisorOrgsRef);
const { data: activeAccounts, pending: loadingAccounts } =
  useCollection<Account>(activeAccountsRef);

const activeAccountsCountBySupervisorOrgId = computed(() => {
  const counts = new Map<string, number>();
  for (const account of activeAccounts.value ?? []) {
    if (!account.supervisorOrgId) continue;
    counts.set(account.supervisorOrgId, (counts.get(account.supervisorOrgId) ?? 0) + 1);
  }
  return counts;
});

const supervisorOrgsWithActiveAccountsCount = computed(() => {
  return (supervisorOrgs.value ?? []).map((supervisorOrg) => ({
    ...supervisorOrg,
    id: supervisorOrg.id,
    activeAccountsCount: activeAccountsCountBySupervisorOrgId.value.get(supervisorOrg.id) ?? 0,
  }));
});

const loading = computed(() => loadingSupervisorOrgs.value || loadingAccounts.value);
</script>
